import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "./api";

const StoreCtx = createContext(null);
export const useStore = () => useContext(StoreCtx);

export function StoreProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("me_cart") || "[]"); } catch { return []; }
  });
  const [zoneId, setZoneId] = useState(localStorage.getItem("me_zone") || "");

  useEffect(() => {
    // CRITICAL: if returning from Google OAuth callback, skip /me — AuthCallback handles it.
    if (window.location.hash && window.location.hash.includes("session_id=")) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => { localStorage.setItem("me_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { if (zoneId) localStorage.setItem("me_zone", zoneId); }, [zoneId]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("me_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("me_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
    localStorage.setItem("me_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("me_token");
    setUser(false);
    setCart([]);
  }, []);

  const addToCart = useCallback((product, qty) => {
    const snap = {
      product_id: product.id, name: product.name, sku: product.sku, brand: product.brand,
      price: product.price, unit: product.unit, image: product.image,
      vendor_id: product.vendor_id, vendor_name: product.vendor_name || "",
      moq: product.moq || 1, express_eligible: product.express_eligible,
    };
    setCart((prev) => {
      if (prev.length && prev[0].vendor_id !== snap.vendor_id) {
        toast.info("Single-vendor fulfilment: cart switched to this vendor.");
        return [{ ...snap, qty: Math.max(qty || snap.moq, snap.moq) }];
      }
      const ex = prev.find((c) => c.product_id === snap.product_id);
      if (ex) return prev.map((c) => c.product_id === snap.product_id ? { ...c, qty: c.qty + (qty || snap.moq) } : c);
      return [...prev, { ...snap, qty: Math.max(qty || snap.moq, snap.moq) }];
    });
    toast.success(`${product.name} added to cart`);
  }, []);

  const setQty = useCallback((pid, qty) => {
    setCart((prev) => prev.map((c) => c.product_id === pid ? { ...c, qty: Math.max(qty, c.moq || 1) } : c));
  }, []);

  const removeItem = useCallback((pid) => {
    setCart((prev) => prev.filter((c) => c.product_id !== pid));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  return (
    <StoreCtx.Provider value={{
      user, setUser, login, register, googleLogin, logout,
      cart, addToCart, setQty, removeItem, clearCart,
      cartCount: cart.reduce((s, c) => s + c.qty, 0),
      zoneId, setZoneId,
    }}>
      {children}
    </StoreCtx.Provider>
  );
}

export { formatApiError };
